// src/components/TestServiceReportsPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  listServiceCategories,
  getPriceService,
  createOrderService,
  verifyAndGenerateService,
  getReportDownloadUrl,
  generateReportWithCombo,
} from '../tmApi';
import Layout from './Layout';
import './TestReportsCommon.css';

import { auth } from '../firebaseConfig';
import { consumeQuota } from '../quotaManager';

export default function TestServiceReportsPage() {
  const [cats, setCats] = useState([]);
  const [query, setQuery] = useState('');
  const [slug, setSlug] = useState('');
  const [selectedName, setSelectedName] = useState('');
  const [availability, setAvailability] = useState(null);

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const [pricing, setPricing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [downloadUrl, setDownloadUrl] = useState('');

  // combobox
  const [open, setOpen] = useState(false);
  const [hoverIdx, setHoverIdx] = useState(-1);
  const boxRef = useRef(null);

  const filtered = useMemo(() => {
    const s = (query || '').trim().toLowerCase();
    if (!s) return cats;
    return cats.filter((c) =>
      (c.name || '').toLowerCase().includes(s) ||
      (c.slug || '').toLowerCase().includes(s)
    );
  }, [cats, query]);

  // load service categories (includes availability in metadata)
  useEffect(() => {
    (async () => {
      try {
        const data = await listServiceCategories();
        setCats(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
        setMessage('Failed to load service categories');
      }
    })();
  }, []);

  // click-outside to close dropdown
  useEffect(() => {
    function onDocClick(e) {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  function chooseCategory(cat) {
    setSlug(cat.slug);
    setSelectedName(cat.name);
    setQuery(cat.name);
    setOpen(false);
    setHoverIdx(-1);
    setMessage('');

    const a = cat.available || {};
    setAvailability(a);
    setFrom(a.min || '');
    setTo(a.max || '');
    setPricing(null);
    setDownloadUrl('');
  }

  function clearSelection() {
    setSlug('');
    setSelectedName('');
    setQuery('');
    setAvailability(null);
    setFrom('');
    setTo('');
    setPricing(null);
    setDownloadUrl('');
    setMessage('');
  }

  async function handlePrice() {
    if (!slug || !from || !to) return;
    setBusy(true);
    setMessage('');
    setPricing(null);
    try {
      const p = await getPriceService({ slug, date_from: from, date_to: to });
      setPricing(p);
    } catch (e) {
      console.error(e);
      setMessage('Pricing failed');
    } finally {
      setBusy(false);
    }
  }

  // 🔵 EXISTING: PAY VIA RAZORPAY – unchanged logic, just tidied slightly
  async function handlePay() {
    if (!pricing) return;
    setBusy(true);
    setMessage('');
    setDownloadUrl('');

    try {
      // 1) Create order
      const order = await createOrderService({
        slug,
        date_from: from,
        date_to: to,
      });

      // 2) Best-effort prefill from Firebase user
      let contact = '';
      let email = '';
      try {
        const { getAuth } = await import('firebase/auth');
        const u = getAuth().currentUser;
        contact = u?.phoneNumber || '';
        email = u?.email || '';
      } catch {}

      // 3) Detect iOS Safari and force UPI "collect"
      const ua = navigator.userAgent || '';
      const isIOS = /iP(hone|ad|od)/.test(ua);
      const isSafari = /^((?!chrome|android).)*safari/i.test(ua);
      const iOSSafari = isIOS && isSafari;

      const rzpOptions = {
        key: order.key_id,
        amount: order.amount,
        currency: order.currency,
        name: 'Tender Managers',
        description: `Service Report: ${slug} (${from} → ${to})`,
        order_id: order.order_id,
        prefill: { contact, email },
        ...(iOSSafari
          ? {
              config: {
                upi: { flow: 'collect' },
                display: { hide: [{ method: 'upi_intent' }] },
              },
            }
          : {}),
        handler: async function (response) {
          try {
            const result = await verifyAndGenerateService({
              slug,
              date_from: from,
              date_to: to,
              order_id: response.razorpay_order_id,
              payment_id: response.razorpay_payment_id,
              signature: response.razorpay_signature,
            });
            const d = await getReportDownloadUrl(result.purchaseId);
            setDownloadUrl(d.url);
            setMessage('Report is ready ✅');
          } catch (err) {
            console.error(err);
            setMessage('Verification failed');
          }
        },
        theme: { color: '#0ea5e9' },
        modal: { ondismiss: () => setMessage('Payment window closed') },
      };

      const rzp = new window.Razorpay(rzpOptions);
      rzp.open();
    } catch (e) {
      console.error(e);
      setMessage('Could not create order');
    } finally {
      setBusy(false);
    }
  }

  // Use plan quota (no Razorpay)
  async function handleQuotaGenerate() {
    if (!pricing) { setMessage('Please click "Get Price" first.'); return; }
    if (!slug || !from || !to) { setMessage('Select category and date range first.'); return; }

    const rowsNeeded = pricing.rows || 0;
    if (!rowsNeeded) { setMessage('Could not determine rows for this report.'); return; }

    setBusy(true);
    setMessage('');
    setDownloadUrl('');

    try {
      const u = auth.currentUser;
      if (!u || !u.phoneNumber) { setMessage('Please login to use plan quota.'); setBusy(false); return; }

      const result = await consumeQuota('reportRows', rowsNeeded);
      if (!result.ok) {
        setMessage(`${result.reason} You can still use Pay & Generate instead.`);
        setBusy(false);
        return;
      }

      const gen = await generateReportWithCombo({
        dataset: 'service', slug, date_from: from, date_to: to, filters: null, rows: rowsNeeded,
      });
      const d = await getReportDownloadUrl(gen.purchaseId);
      setDownloadUrl(d.url);
      setMessage('Report generated using your plan quota.');
    } catch (e) {
      console.error(e);
      setMessage('Could not generate report using plan quota.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Layout>
      <div style={{ maxWidth: 900, margin: '24px auto', padding: 16 }}>
        <h2 style={{ marginBottom: 12 }}>Service Category Reports</h2>

        {/* Search / select */}
        <div ref={boxRef} style={{ position: 'relative', marginBottom: 16 }}>
          <label
            style={{ display: 'block', fontWeight: 600, marginBottom: 6 }}
          >
            Search / select service category
          </label>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              border: '1px solid #cbd5e1',
              borderRadius: 8,
              padding: '6px 10px',
              background: '#fff',
            }}
            onClick={() => setOpen(true)}
          >
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
                setHoverIdx(0);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={(e) => {
                if (!open && (e.key === 'ArrowDown' || e.key === 'Enter')) {
                  setOpen(true);
                  return;
                }
                if (!open) return;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  setHoverIdx((i) =>
                    Math.min((i < 0 ? -1 : i) + 1, filtered.length - 1)
                  );
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  setHoverIdx((i) =>
                    Math.max((i < 0 ? filtered.length : i) - 1, 0)
                  );
                } else if (e.key === 'Enter') {
                  e.preventDefault();
                  const item = filtered[Math.max(hoverIdx, 0)];
                  if (item) chooseCategory(item);
                } else if (e.key === 'Escape') {
                  setOpen(false);
                }
              }}
              placeholder="Type to search…"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                fontSize: 14,
                padding: '6px 4px',
              }}
            />
            {slug ? (
              <button
                onClick={clearSelection}
                type="button"
                title="Clear"
                style={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontSize: 18,
                  lineHeight: 1,
                  padding: '2px 4px',
                  color: '#64748b',
                }}
              >
                ×
              </button>
            ) : (
              <span style={{ fontSize: 12, color: '#64748b' }}>▼</span>
            )}
          </div>

          {open && filtered.length > 0 && (
            <div
              style={{
                position: 'absolute',
                zIndex: 10,
                left: 0,
                right: 0,
                background: '#fff',
                border: '1px solid #cbd5e1',
                borderTop: 'none',
                borderRadius: '0 0 8px 8px',
                maxHeight: 320,
                overflowY: 'auto',
                boxShadow: '0 6px 18px rgba(2,8,23,0.08)',
              }}
            >
              {filtered.map((c, idx) => (
                <div
                  key={c.slug}
                  onMouseEnter={() => setHoverIdx(idx)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => chooseCategory(c)}
                  style={{
                    padding: '10px 12px',
                    cursor: 'pointer',
                    background: idx === hoverIdx ? '#f1f5f9' : '#fff',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    gap: 8,
                  }}
                >
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      {c.name}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {c.slug}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: '#475569' }}>
                    {c.rows_count?.toLocaleString?.() ?? c.rows_count} rows
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected + availability */}
        {slug && (
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                background: '#eef2ff',
                color: '#3730a3',
                border: '1px solid #c7d2fe',
                padding: '6px 10px',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              Service Category: {selectedName || slug}
            </div>
            <div style={{ fontSize: 14, color: '#555' }}>
              Available: <b>{availability?.min || '-'}</b> to{' '}
              <b>{availability?.max || '-'}</b>
            </div>
          </div>
        )}

        {/* Date range */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 12,
            marginBottom: 12,
          }}
        >
          <div>
            <label style={{ display: 'block', fontWeight: 600 }}>From</label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid #ccc',
                borderRadius: 8,
              }}
              min={availability?.min || undefined}
              max={availability?.max || undefined}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontWeight: 600 }}>To</label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              style={{
                width: '100%',
                padding: 8,
                border: '1px solid #ccc',
                borderRadius: 8,
              }}
              min={availability?.min || undefined}
              max={availability?.max || undefined}
            />
          </div>
        </div>

        {/* Actions – THREE buttons now */}
        <div
          style={{
            display: 'flex',
            gap: 12,
            flexWrap: 'wrap',
            marginBottom: 12,
          }}
        >
          <button
            disabled={!slug || !from || !to || busy}
            onClick={handlePrice}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #0ea5e9',
              background: '#e0f2fe',
              opacity: !slug || !from || !to || busy ? 0.6 : 1,
              cursor:
                !slug || !from || !to || busy ? 'not-allowed' : 'pointer',
            }}
          >
            {busy ? 'Please wait…' : 'Get Price'}
          </button>

          <button
            disabled={!pricing || busy}
            onClick={handlePay}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #16a34a',
              background: '#dcfce7',
              opacity: !pricing || busy ? 0.6 : 1,
              cursor: !pricing || busy ? 'not-allowed' : 'pointer',
            }}
          >
            Pay & Generate Report
          </button>

          <button
            disabled={!pricing || busy}
            onClick={handleQuotaGenerate}
            style={{
              padding: '10px 14px',
              borderRadius: 8,
              border: '1px solid #4f46e5',
              background: '#e0e7ff',
              opacity: !pricing || busy ? 0.6 : 1,
              cursor: !pricing || busy ? 'not-allowed' : 'pointer',
            }}
          >
            Use Plan Quota
          </button>
        </div>

        {/* Pricing */}
        {pricing && (
          <div style={{ marginBottom: 12, fontSize: 15 }}>
            Rows: <b>{pricing.rows.toLocaleString()}</b> &nbsp;|&nbsp; Amount: ₹{' '}
            <b>{Number(pricing.amount_rupees).toFixed(2)}</b>
          </div>
        )}

        {/* Download link */}
        {downloadUrl && (
          <div style={{ marginTop: 12 }}>
            <a href={downloadUrl} target="_blank" rel="noreferrer">
              Download your report
            </a>
            <div style={{ fontSize: 12, color: '#666' }}>
              (link valid for 1 hour)
            </div>
          </div>
        )}

        {/* Messages */}
        {message && (
          <div style={{ marginTop: 12, color: '#444' }}>{message}</div>
        )}

        {/* Help */}
        <div className="report-help">
          Can’t find your category or need a custom cut? Contact us:
          <div className="contact-actions">
            <a
              href="tel:+918655654720"
              className="contact-chip phone"
              title="Call us"
            >
              <span className="icon">☎</span>
              <span>+91 8655654720</span>
            </a>
            <a
              href="mailto:info@tendermanagers.com"
              className="contact-chip email"
              title="Email us"
            >
              <span className="icon">✉</span>
              <span>info@tendermanagers.com</span>
            </a>
            <a
              className="contact-chip whatsapp"
              href="https://wa.me/8655654720?text=Hi%20Tender%20Managers%2C%20I%27d%20like%20help%20with%20a%20report."
              target="_blank"
              rel="noreferrer"
              title="Chat on WhatsApp"
            >
              <span className="icon">💬</span>
              <span>WhatsApp</span>
            </a>
          </div>
        </div>
      </div>
    </Layout>
  );
}
